import type { GeneratedCtaCluster } from "~/lib/onboarding/marketplace-onboarding-defaults";
import { DEFAULT_TEAM_HANDOFF_CTAS } from "~/lib/onboarding/marketplace-onboarding-defaults";
import type { IndustryVertical } from "~/lib/utils/industry-profile";

export type HireTeamHandoffCtaCluster = GeneratedCtaCluster;

export function buildDefaultTeamHandoffCtaClustersForHire(
  vertical: IndustryVertical,
  contentText: string,
  businessName: string,
): HireTeamHandoffCtaCluster[] {
  const lower = contentText.toLowerCase();
  const isDental = /dental|dentist|orthodont|oral surgery|periodont/.test(lower);
  const isMedical =
    vertical === "healthcare" ||
    /physical therapy|physiotherapy|chiropractic|clinic|medical/.test(lower);
  const isLegal = vertical === "legal" || /law firm|attorney|lawyer| legal /.test(lower);

  if (isDental) {
    return [
      {
        ctaQuestion: "Verify Insurance",
        qualificationQuestions: [
          "Which insurance provider do you have?",
          "What is your member ID or group number?",
          "What is the best email to send coverage details to?",
        ],
        locationRouting: "all",
      },
      {
        ctaQuestion: "Connect with Staff",
        qualificationQuestions: [
          "What would you like to discuss with our team?",
          "What is the best phone number to reach you?",
          "When is a good time for us to follow up?",
        ],
        locationRouting: "all",
      },
    ];
  }

  if (isMedical) {
    return [
      {
        ctaQuestion: "Connect with Staff",
        qualificationQuestions: [
          "How can our team help you today?",
          "What is the best way to reach you?",
          "When would you like us to follow up?",
        ],
        locationRouting: "all",
      },
      {
        ctaQuestion: "Request Appointment",
        qualificationQuestions: [
          "What type of visit do you need?",
          "What days or times work best for you?",
          "Is this your first visit to our office?",
        ],
        locationRouting: "all",
      },
    ];
  }

  const isEducation =
    vertical === "education" ||
    /\b(school|academy|university|college|enrollment|admissions|student)\b/.test(lower);

  if (isEducation) {
    return [
      {
        ctaQuestion: "Schedule a Tour",
        qualificationQuestions: [
          "Are you interested in a campus visit or virtual tour?",
          "What grade level or program are you exploring?",
          "What is the best email to send tour details to?",
        ],
        locationRouting: "all",
      },
      {
        ctaQuestion: "Contact Admissions",
        qualificationQuestions: [
          "What would you like to know about our programs?",
          "What is the best way to reach you?",
          "When would be a good time for our admissions team to follow up?",
        ],
        locationRouting: "all",
      },
    ];
  }

  if (isLegal) {
    return [
      {
        ctaQuestion: "Schedule Consultation",
        qualificationQuestions: [
          "What type of legal matter do you need help with?",
          "What is your preferred contact method?",
          "When would you like to speak with our team?",
        ],
        locationRouting: "all",
      },
      {
        ctaQuestion: "Connect with Staff",
        qualificationQuestions: [
          "What would you like to discuss?",
          "What is the best phone number to reach you?",
          "When is a good time for a callback?",
        ],
        locationRouting: "all",
      },
    ];
  }

  return DEFAULT_TEAM_HANDOFF_CTAS.map((cluster) => ({
    ...cluster,
    qualificationQuestions: cluster.qualificationQuestions.map((q) =>
      q.replace(/our team/i, businessName ? `${businessName}'s team` : "our team"),
    ),
  }));
}

export function normalizeHireTeamHandoffCtaClusters(
  clusters: HireTeamHandoffCtaCluster[] | undefined,
  fallback: HireTeamHandoffCtaCluster[],
): HireTeamHandoffCtaCluster[] {
  const source = clusters?.length ? clusters : fallback;
  return source
    .map((cluster) => ({
      ctaQuestion: cluster.ctaQuestion?.trim() ?? "",
      qualificationQuestions: (cluster.qualificationQuestions ?? [])
        .map((q) => q.trim())
        .filter(Boolean),
      locationRouting: cluster.locationRouting?.trim() || "all",
    }))
    .filter((cluster) => cluster.ctaQuestion.length > 0)
    .slice(0, 6);
}
